import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DropdownService } from '../../../shared/services/dropdown.service';
import {
  defaultDropdownPlaceholderText,
  DropdownConfiguration,
  formControlDisableStatus,
} from '@zipari/shared-ds-util-form';
import { isEqual } from 'lodash';
import { getUpdatedControlValue, syncHiddenControl } from './dropdown.helper';

const requireAtLeastTwoOptions = 2;

@Component({
  selector: 'control-dropdown',
  styleUrls: ['./dropdown.component.scss'],
  templateUrl: './dropdown.component.html',
})
export class ControlDropdownComponent implements OnInit, OnChanges {
  @Input() config: DropdownConfiguration;
  @Input() control: UntypedFormControl;
  @Input() selectedIndex: number;
  public dropdownDisplayControl: UntypedFormControl = new UntypedFormControl();

  oneOptionNotIncludingPlaceholder = requireAtLeastTwoOptions;

  constructor(private dropdownService: DropdownService) {}

  public get isDisabled() {
    return this.config.isDisabled || this.control.disabled;
  }

  ngOnInit(): void {
    this.intializeOptions();
  }

  intializeOptions() {
    this.undefinedToNull();
    this.setPlaceholder();

    this.dropdownDisplayControl.valueChanges.subscribe(
      (value: string | string[]) => {
        const transformedValue = syncHiddenControl(this.config, value);

        if (!isEqual(this.control.value, transformedValue)) {
          this.control.setValue(transformedValue);
          this.control.markAsTouched();
          this.control.markAsDirty();
        }
      },
    );

    const updatedValue = getUpdatedControlValue(this.config, this.control);

    updatedValue && this.dropdownDisplayControl.setValue(updatedValue);

    this.control.statusChanges.subscribe((status: string) => {
      const method = status === formControlDisableStatus ? 'disable' : 'enable';

      this.dropdownDisplayControl[method]({
        onlySelf: true,
        emitEvent: false,
      });
    });

    this.control.valueChanges.subscribe(() => {
      const controlValue = getUpdatedControlValue(this.config, this.control);

      this.dropdownDisplayControl.setValue(controlValue, { emitEvent: false });
    });
  }

  /** Accessibility and display */
  undefinedToNull() {
    if (this.dropdownDisplayControl.value === undefined) {
      this.dropdownDisplayControl.setValue(null);
    }
  }

  setPlaceholder() {
    const options = this.config?.options;

    if (
      options &&
      Object.isExtensible(this.config) &&
      // This is actually necessary - otherwise it checks falsy, not "present and falsy"
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
      this.config.usePlaceholder !== false
    ) {
      const noneAreNull = !options?.some((option) => option.value === null);
      const hasOneOption = options.length === 1;
      const hidePlaceholderIfOneOption =
        !!this.config.hidePlaceholderIfOneOption && hasOneOption;
      const ifOneOptionShowStaticText =
        !!this.config.ifOneOptionShowStaticText && hasOneOption;
      const addPlaceholder =
        noneAreNull &&
        !(hidePlaceholderIfOneOption || ifOneOptionShowStaticText);

      if (addPlaceholder) {
        const placeholder =
          this.config.placeholder || defaultDropdownPlaceholderText;
        const placeholderOption = {
          label: placeholder,
          value: null,
        };

        this.config = {
          ...this.config,
          options: this.config.putPlaceholderDefaultAsLastItem
            ? [...options, placeholderOption]
            : [placeholderOption, ...options],
        };
      }
    }
  }

  hideOrShowOptions() {
    return (
      this.config.hideIfOnlyOneOption &&
      this.config.options.length > this.oneOptionNotIncludingPlaceholder
    );
  }

  onOptionSelected(event, config) {
    this.dropdownService.announceChange({ event: event, config: config });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.config) {
      this.intializeOptions();
    }
  }
}
